import { FC, useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { capitalCase } from 'change-case';
import { NumberUtils, Tr } from '@utils';
import { baseEntityComparator } from '@models';
import { Product, QuotationItem } from '@services';
import { Stack, Tooltip, Typography, TypographyProps } from '@mui/material';
import { DataTable, FormikNumber, VoltaageCheckbox } from '@components/common';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { instanceToInstance, plainToInstance } from 'class-transformer';

const Body1: FC<TypographyProps> = ({ sx, ...props }) => <Typography variant="body2" sx={{ ...sx, wordBreak: 'keep-all' }} {...props} />;

type MergedEntity = QuotationItem & { checkboxState?: 'checked' | 'unchecked' | 'indeterminate' };

export interface IQuotationItemsTableProps {
	quotationItems: QuotationItem[];
	products: Product[];
	notEditable?: boolean;
	onEdit?: (id: string, data: Partial<QuotationItem>) => void;
	onAdd?: (data: Partial<QuotationItem>) => void;
}

export const QuotationItemsTable: FC<IQuotationItemsTableProps> = ({ quotationItems, products, notEditable, onEdit, onAdd }) => {
	const [mergedEntities, setMergedEntities] = useState<Record<string, MergedEntity>>({});
	const columns = useMemo<ColumnDef<MergedEntity>[]>(
		() => [
			{
				accessorKey: 'checkboxState',
				header: () => '',
				enableSorting: false,
				size: 0.01,
				cell: ({ row }) => (
					<VoltaageCheckbox
						sx={{ width: 40, height: 40 }}
						size="small"
						name="entity-list-select-checkbox"
						data-testid="entity-list-select-checkbox"
						onChange={() =>
							row.original.id
								? onEdit?.(row.original.id, { checkout: !row.original.checkout })
								: onAdd?.({
										productId: row.original.productId,
										numberOfItems: row.original.numberOfItems,
										discount: row.original.discount,
										checkout: !row.original.checkout
								  })
						}
						checked={row.original.checkboxState === 'checked'}
						indeterminate={row.original.checkboxState === 'indeterminate'}
						disabled={notEditable}
					/>
				)
			},
			{
				accessorKey: 'productName',
				header: () => <Tr.Portal path="quotation.product-name" />,
				enableSorting: false,
				cell: ({ row }) => <Body1>{row.original.productName || ''}</Body1>
			},
			{
				accessorKey: 'productCategory',
				header: () => <Tr.Portal path="quotation.product-category" />,
				enableSorting: false,
				cell: ({ row }) => <Body1>{capitalCase(row.original.productCategory || '')}</Body1>
			},
			{
				accessorKey: 'numberOfItems',
				header: () => <Tr.Portal path="quotation.number-of-items" />,
				enableSorting: false,
				cell: ({ row }) => (
					<Stack direction="row" spacing={1} alignItems="center">
						<FormikNumber
							withoutFormik
							disabled={notEditable}
							name=""
							size="small"
							value={row.original.numberOfItems}
							onBlur={(e) =>
								row.original.id
									? onEdit?.(row.original.id, { numberOfItems: +e.target.value })
									: onAdd?.({
											productId: row.original.productId,
											numberOfItems: +e.target.value,
											discount: row.original.discount,
											checkout: true
									  })
							}
							decimal={0}
						/>

						{notEditable ? (
							<Tooltip
								title={<Tr.Portal path="quotation.please-select-parking-area" />}
								arrow
								placement="top"
								componentsProps={{ tooltip: { sx: { padding: 1 } } }}
							>
								<InfoOutlinedIcon fontSize="small" color="inherit" />
							</Tooltip>
						) : null}
					</Stack>
				)
			},
			{
				accessorKey: 'unitPrice',
				header: () => <Tr.Portal path="quotation.unit-price" />,
				enableSorting: false,
				cell: ({ row }) => (
					<Body1>
						{NumberUtils.formatAsPrice(row.original.unitPrice, {
							prefix: row.original.currencySymbol || '',
							pureDefault: true
						})}
					</Body1>
				)
			},
			{
				accessorKey: 'productInstallationCost',
				header: () => <Tr.Portal path="quotation.installation-cost" />,
				enableSorting: false,
				cell: ({ row }) => (
					<Body1>
						{NumberUtils.formatAsPrice(row.original.productInstallationCost, {
							prefix: row.original.currencySymbol || '',
							pureDefault: true
						})}
					</Body1>
				)
			},
			{
				accessorKey: 'amount',
				header: () => <Tr.Portal path="quotation.total-price" />,
				enableSorting: false,
				cell: ({ row }) => (
					<Body1>
						{NumberUtils.formatAsPrice(row.original.amount, { prefix: row.original.currencySymbol || '', pureDefault: true })}
					</Body1>
				)
			},
			{
				accessorKey: 'discount',
				header: () => <Tr.Portal path="quotation.discount" />,
				enableSorting: false,
				cell: ({ row }) => <Body1>{NumberUtils.format(row.original.discount, '0.[00] %')}</Body1>
			},
			{
				accessorKey: 'totalPriceAfterDiscount',
				header: () => <Tr.Portal path="quotation.total-price-after-discount" />,
				enableSorting: false,
				cell: ({ row }) => (
					<Body1>
						{NumberUtils.formatAsPrice(row.original.totalPriceAfterDiscount, {
							prefix: row.original.currencySymbol || '',
							pureDefault: true
						})}
					</Body1>
				)
			}
		],
		[onEdit, onAdd, notEditable]
	);

	useEffect(() => {
		const entities = (quotationItems || []).reduce((obj, current) => {
			const { productId, amount, numberOfItems, checkout } = current;
			if (obj[productId]) {
				if (checkout) {
					obj[productId].amount += amount;
					obj[productId].numberOfItems += numberOfItems;

					if (obj[productId].checkboxState === 'unchecked' || obj[productId].checkboxState === 'indeterminate') {
						obj[productId].checkboxState = 'indeterminate';
					} else {
						obj[productId].checkboxState = 'checked';
					}
				} else {
					if (obj[productId].checkboxState === 'checked' || obj[productId].checkboxState === 'indeterminate') {
						obj[productId].checkboxState = 'indeterminate';
					} else {
						obj[productId].checkboxState = 'unchecked';
					}
				}
			} else {
				obj[productId] = instanceToInstance(current);
				obj[productId].checkboxState = checkout ? 'checked' : 'unchecked';
			}
			return obj;
		}, {} as Record<string, MergedEntity>);

		products.forEach((product) => {
			const { id } = product;
			if (!entities[id]) {
				entities[id] = plainToInstance(QuotationItem, {
					amount: 0,
					numberOfItems: 0,
					discount: 0,
					productId: id,
					product,
					checkout: false,
					checkboxState: 'unchecked'
				});
			}
		});

		setMergedEntities(entities);
	}, [quotationItems, products]);

	return (
		<DataTable
			columns={columns}
			data={Object.values(mergedEntities).sort((a, b) => a.productName.localeCompare(b.productName))}
			comparator={baseEntityComparator}
			variant="elevation"
			elevation={0}
			headerTypographyProps={{ variant: 'body2', fontWeight: 600 }}
			headerCellStyle={(_column, colIndex) => ({
				paddingX: colIndex === 0 ? 0.5 : 1.5,
				paddingTop: 6,
				paddingBottom: 1,
				backgroundColor: 'common.white',
				color: 'common.black'
			})}
			bodyCellStyle={(cell, rowIndex, colIndex) => ({
				borderTopLeftRadius: colIndex === 0 ? 24 : 0,
				borderBottomLeftRadius: colIndex === 0 ? 24 : 0,
				borderTopRightRadius: colIndex === cell.row.getAllCells().length - 1 ? 24 : 0,
				borderBottomRightRadius: colIndex === cell.row.getAllCells().length - 1 ? 24 : 0,
				border: 'none'
			})}
		/>
	);
};
